/**
 * Where a visit began, kept until a guest is made. The landing pages are plain HTML and the app is
 * another page load, so nothing in memory survives the click from one to the other: an inline
 * script, put in the head of every page by vite.config.ts, notes the first page of the visit in
 * sessionStorage (per tab, gone when it closes, never a cookie). `firstTouch` reads it back for
 * the one request that asks, `POST /api/auth/guest`, where the api keeps a site, a path and the
 * UTM tags, never a full URL or an address (features/events/arrival.ts).
 *
 * Student pages (`/l/`, `/w/`) note nothing. Nothing here runs in the api's name: the server
 * trims and checks every field again.
 */

const KEY = 'tt-first'

/** The body `POST /api/auth/guest` takes as `arrival`. Empty strings mean "absent". */
export type FirstTouch = {
	landing: string
	referrer: string
	utmSource: string
	utmMedium: string
	utmCampaign: string
}

/**
 * Runs once per page load. Only the first page of a tab's
 * visit is kept; a referrer from the site itself is no referrer. It may not throw: storage is
 * blocked in some private modes.
 */
export const FIRST_TOUCH_SNIPPET = `try{var s=sessionStorage;if(!/^\\/[lw]\\//.test(location.pathname)&&!s.getItem('${KEY}')){var q=new URLSearchParams(location.search),h='';try{h=new URL(document.referrer).hostname}catch(e){}s.setItem('${KEY}',JSON.stringify({landing:location.pathname,referrer:h===location.hostname?'':h,utmSource:q.get('utm_source')||'',utmMedium:q.get('utm_medium')||'',utmCampaign:q.get('utm_campaign')||''}))}}catch(e){}`

/** What the snippet kept for this visit, or nothing (a blocked storage, a direct start on a student page). */
export function firstTouch(storage?: Pick<Storage, 'getItem'>): FirstTouch | undefined {
	try {
		const raw = (storage ?? sessionStorage).getItem(KEY)
		const parsed: unknown = raw ? JSON.parse(raw) : null
		return parsed && typeof parsed === 'object' ? (parsed as FirstTouch) : undefined
	} catch {
		return undefined
	}
}

/**
 * Runs the guest request with where the visit began. An api build that does not know `arrival`
 * yet (a rollback, or a deploy where the api lagged the web) refuses the unknown field with a 400
 * and would turn away every visitor who has a note, so that one answer is retried once without it:
 * starting matters more than where she came from. Nothing is retried when there was no note, or
 * for any other answer.
 */
export async function withArrival<R extends { status: number }>(
	send: (arrival: FirstTouch | undefined) => Promise<R>,
	storage?: Pick<Storage, 'getItem'>,
): Promise<R> {
	const arrival = firstTouch(storage)
	const res = await send(arrival)
	return arrival && res.status === 400 ? send(undefined) : res
}

/**
 * The same page with the snippet at the end of its head (the charset meta has to stay inside the
 * first 1024 bytes); a page without a head is returned as it was.
 */
export const withFirstTouch = (html: string): string =>
	html.replace('</head>', () => `<script>${FIRST_TOUCH_SNIPPET}</script></head>`)
